import Head from 'next/head';
import Router from 'next/router';
import React from 'react';

import moment from 'moment';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';

import Pagination from '@mui/material/Pagination';

import Layout from '../components/layout';

import CompanyModal from '../components/companies/CompanyModal';
import JobDetail from '../components/jobs/JobDetail';
import WeeklyUpdateModal from '../components/common/WeeklyUpdateModal';

import {
  getBatchOfJobsApiMethod,
  getCompanyApiMethod,
  getAllCompaniesApiMethod,
  getFeaturedApiMethod,
} from '../lib/api';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FeaturedJobs from 'components/jobs/Featured';
import FeaturedCompanies from 'components/companies/Featured';

const categories = [
  {
    categoryTitle: 'line',
    categorySlug: 'line',
  },
  {
    categoryTitle: 'Intern, Co-op, Technician',
    categorySlug: 'internship-co-op-technician-research-assistant',
    salaryRange: '40K - 80K',
  },
  {
    categoryTitle: 'Research Associate',
    categorySlug: 'research-associate',
    salaryRange: '60K - 100K',
  },
  {
    categoryTitle: 'Scientist',
    categorySlug: 'scientist',
    salaryRange: '100K - 180K',
  },
  {
    categoryTitle: 'Engineer, Developer, Analyst',
    categorySlug: 'engineer-developer-analyst',
    salaryRange: '100K - 200K',
  },
  {
    categoryTitle: 'line',
    categorySlug: 'line',
  },
  {
    categoryTitle: 'Manager, Supervisor',
    categorySlug: 'manager-supervisor',
    salaryRange: '100K - 180K',
  },
  {
    categoryTitle: 'Director, Head, Lead',
    categorySlug: 'director-head-lead',
    salaryRange: '150K - 300K',
  },
  {
    categoryTitle: 'President, Officer, Executive',
    categorySlug: 'president-officer-executive',
    salaryRange: '300K - 600K',
  },
  {
    categoryTitle: 'line',
    categorySlug: 'line',
  },
  {
    categoryTitle: 'Accountant, Controller',
    categorySlug: 'accountant-controller',
    salaryRange: '60K - 300K',
  },
  {
    categoryTitle: 'Agent, Counsel, Paralegal',
    categorySlug: 'agent-counsel-paralegal',
    salaryRange: '80K - 300K',
  },
  {
    categoryTitle: 'Assistant, Administrator, Coordinator',
    categorySlug: 'assistant-administrator-coordinator',
    salaryRange: '40K - 140K',
  },
  {
    categoryTitle: 'Recruiter',
    categorySlug: 'recruiter',
    salaryRange: '70K - 150K',
  },
  {
    categoryTitle: 'Specialist, Generalist, Partner',
    categorySlug: 'specialist-generalist-partner',
    salaryRange: '60K - 180K',
  },
  {
    categoryTitle: 'Writer, Editor',
    categorySlug: 'writer-editor',
    salaryRange: '70K - 140K',
  },
  {
    categoryTitle: 'line',
    categorySlug: 'line',
  },
  {
    categoryTitle: 'Other roles',
    categorySlug: 'other-roles',
  },
];

const states = [
  'Remote',
  'line',
  'MA',
  'CA',
  'line',
  'all-states',
  'AK',
  'AL',
  'AR',
  'AZ',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
  'Other',
];

const filterSelectMenuProps = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
  PaperProps: {
    style: {
      maxHeight: 480,
    },
  },
};

type Company = {
  _id: string;
  name: string;
  logo: string;
  note: string;
  employeeRange: string;
  fundingRange: string;
  location: string;
  foundationYear: string;
  websiteUrl: string;
  jobBoardUrl: string;
  scriptName: string;
  scriptId: string;
  companySlug: string;
};

const companyNameRank = (name: string) => (/^[A-Za-z]/.test(name || '') ? 0 : 1);

const sortCompaniesLetterFirst = (companies: Company[]) =>
  [...companies].sort((a, b) => {
    const rankDiff = companyNameRank(a.name) - companyNameRank(b.name);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return String(a.name || '').localeCompare(String(b.name || ''));
  });

const categoryLabel = (slug: string) => {
  if (slug === 'All roles' || slug === 'all-categories') {
    return 'All roles';
  }
  return categories.find(c => c.categorySlug === slug)?.categoryTitle || slug;
};

const companyLabel = (slug: string, companies: Company[] | undefined) => {
  if (slug === 'All companies' || slug === 'all-companies') {
    return 'All companies';
  }
  return companies?.find(c => c.companySlug === slug)?.name || slug;
};

const selectedCompanyLinks = (
  selectedCompanySlugs: string[],
  companies: Company[] | undefined,
  companyInfo?: Company | null,
) => {
  if (!selectedCompanySlugs.length || selectedCompanySlugs[0] === 'all-companies') {
    return [];
  }

  return selectedCompanySlugs.map(slug => {
    const match = companies?.find(c => c.companySlug === slug);
    if (match?.name) {
      return { name: match.name, companySlug: match.companySlug };
    }
    if (companyInfo?.companySlug === slug && companyInfo.name) {
      return { name: companyInfo.name, companySlug: companyInfo.companySlug };
    }
    return { name: slug, companySlug: slug };
  });
};

type IFeaturedCompany = {
  _id: string;
  name: string;
  logo: string;
  website: string;
};

type Job = {
  _id: string;
  title: string;
  jobUrl: string;
  location: string;
  datePosted: Date;
  companyName: string;
  companyLogo: string;
  companyId: string;
  isJobRaw: boolean;
  scriptId: string;
  categorySlug: string;
  categoryId: string;
  state: string;
  companySize: string;
  isHighlighted: boolean;
  isPublished: boolean;
  companySlug: string;
  createdAt: Date;
};

type Slot = {
  isAvailable: boolean;
  nearestAvailableDate: Date;
};

type Props = {
  isThemeDark: boolean;
  currentRoute: string;
  batch: Job[];
  numberOfJobs: number;
  error: string;
  companies: Company[];
  pageNumber: number;
  isDataLoadedProp: boolean;
  companyInfo: Company;
  categorySlug: string;
  companySlug: string;
  search: string;
  state: string;
  scriptRanAt: Date;

  featuredJobs: Job[];
  jobsSlotInfo: Slot;

  featuredCompanies: IFeaturedCompany[];
  companiesSlotInfo: Slot;
  numberOfJobsAll: number;
};

type State = {
  showCompanyModal: boolean;
  selectedCompany: Company;
  selectedCategorySlugs: string[];
  selectedCompanySlugs: string[];
  selectedState: string;
  page: number;
  isDataLoaded: boolean;
  selectedStates: string[];
  openNewsletterModal: boolean;
  search?: string;
};

const jobsPerPage = 50;

class MainPage extends React.Component<Props, State> {
  public state = {
    showCompanyModal: false,
    selectedCompany: null,
    showHomepageModal: false,
    selectedCategorySlugs:
      !this.props.categorySlug || this.props.categorySlug === 'all-categories'
        ? ['all-categories']
        : this.props.categorySlug
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
    selectedCompanySlugs:
      !this.props.companySlug || this.props.companySlug === 'all-companies'
        ? ['all-companies']
        : this.props.companySlug
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
    selectedState: this.props.state || 'all-states',
    page: this.props.pageNumber || 1,
    isDataLoaded: this.props.isDataLoadedProp ? true : false,
    selectedStates: this.props.state ? this.props.state.split(',') : ['all-states'],
    openNewsletterModal: false,
    search: this.props.search || '',
  };

  public static async getInitialProps(ctx) {
    const { category, company, state, page, search } = ctx.query;

    const pageNumber = parseInt(page as string, 10);

    const data1 = await getBatchOfJobsApiMethod(
      category ? category : 'all-categories',
      company ? company : 'all-companies',
      state ? state : 'all-states',
      page === undefined ? 1 : pageNumber,
      search,
      ctx.req ? ctx.req.headers : {},
    );

    const data2 = await getAllCompaniesApiMethod(ctx.req ? ctx.req.headers : {});
    const data3 = await getFeaturedApiMethod(
      ctx.req
        ? ctx.req.headers
        : {
            Cookie: ctx.req && ctx.req.headers.cookie,
          },
    );

    if (data1 && !data1.error && data2 && !data2.error) {
      const { batch, numberOfJobs, companyInfo, scriptRanAt, numberOfJobsAll } = data1;

      const { companies } = data2;

      return {
        batch,
        categorySlug: category ? category : 'all-categories',
        companySlug: company ? company : 'all-companies',
        state: state ? state : 'all-states',
        search,
        numberOfJobs,
        companies,
        isDataLoadedProp: true,
        pageNumber: page === undefined ? 1 : pageNumber,
        companyInfo,
        scriptRanAt,

        featuredJobs: data3.jobs || [],
        jobsSlotInfo: data3.jobsSlotInfo,
        featuredCompanies: data3.companies || [],
        companiesSlotInfo: data3.companiesSlotInfo,
        numberOfJobsAll,
      };
    } else {
      return { error: data1.error };
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const { categorySlug, companySlug, state, pageNumber, isDataLoadedProp, search } = this.props;

    if (
      prevProps.categorySlug !== categorySlug ||
      prevProps.companySlug !== companySlug ||
      prevProps.state !== state ||
      prevProps.search !== search
    ) {
      this.setState({
        selectedCategorySlugs:
          !categorySlug || categorySlug === 'all-categories'
            ? ['all-categories']
            : categorySlug
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
        selectedCompanySlugs:
          !companySlug || companySlug === 'all-companies'
            ? ['all-companies']
            : companySlug
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
        selectedState: state,
        selectedStates: state ? state.split(',') : ['all-states'],
        search: search || '',
        isDataLoaded: isDataLoadedProp,
      });
    }

    if (prevProps.pageNumber !== pageNumber) {
      this.setState({ page: pageNumber, isDataLoaded: isDataLoadedProp });
    }
  }

  public render() {
    const {
      batch,
      companies,
      numberOfJobs,
      error,
      companySlug,
      companyInfo,
      categorySlug,
      state,

      featuredJobs,
      jobsSlotInfo,

      featuredCompanies,
      companiesSlotInfo,
      numberOfJobsAll,
    } = this.props;

    const { page, isDataLoaded } = this.state;

    const count = Math.ceil(numberOfJobs / jobsPerPage);
    const emptyStateCompanyLinks = selectedCompanyLinks(
      this.state.selectedCompanySlugs,
      companies,
      companyInfo,
    );

    let headContent = (
      <Head>
        <title>Work In Biotech - All startup biotech jobs. Checked daily.</title>
        <meta name="description" content="Most popular job board for biotech startups." />
        <meta name="robots" content="index, follow" />
      </Head>
    );

    const firstCategorySlug =
      categorySlug && categorySlug !== 'all-categories' ? categorySlug.split(',')[0].trim() : null;
    const categoryTitle =
      firstCategorySlug && categories.find(c => c.categorySlug === firstCategorySlug)?.categoryTitle;

    if (
      categorySlug &&
      categorySlug !== 'all-categories' &&
      (!companySlug || companySlug === 'all-companies') &&
      (!state || state === 'all-states')
    ) {
      if (firstCategorySlug !== 'other-roles') {
        headContent = (
          <Head>
            <title>{categoryTitle} | Work in biotech</title>
            <meta name="description" content={`Find all ${categoryTitle} jobs in biotech startups.`} />
            <meta name="robots" content="index, follow" />
          </Head>
        );
      }
    } else if (
      companySlug &&
      companySlug !== 'all-companies' &&
      (!categorySlug || categorySlug === 'all-categories') &&
      (!state || state === 'all-states')
    ) {
      headContent = (
        <Head>
          <title>{companyInfo && companyInfo.name} | Work in biotech</title>
          <meta name="description" content={`Find all jobs in ${companyInfo && companyInfo.name} company.`} />
          <meta name="robots" content="index, follow" />
        </Head>
      );
    } else if (
      state &&
      state !== 'all-states' &&
      (!categorySlug || categorySlug === 'all-categories') &&
      (!companySlug || companySlug === 'all-companies')
    ) {
      const selectedState = state && state !== 'all-states' && states.find(s => s === state);

      if (selectedState !== 'Remote' && selectedState !== 'Other') {
        headContent = (
          <Head>
            <title>{`Biotech startup jobs in ${selectedState} state | Work in biotech`}</title>
            <meta name="description" content={`Find all biotech startup jobs in ${selectedState} state.`} />
            <meta name="robots" content="index, follow" />
          </Head>
        );
      }

      if (selectedState === 'Remote') {
        headContent = (
          <Head>
            <title>{`Remote biotech startups jobs | Work in biotech`}</title>
            <meta name="description" content={`Find all remote jobs in biotech startups.`} />
            <meta name="robots" content="index, follow" />
          </Head>
        );
      }
    }

    const { selectedStates, search } = this.state;

    const local = moment(new Date(this.props.scriptRanAt)).local().format('MM/DD');

    return (
      <Layout {...this.props}>
        <div style={{ textAlign: 'center', minHeight: '100%' }}>
          {headContent}
          <Box
            sx={{
              margin: { xs: '0px 1%', md: '0px 20%' },
              padding: '10px 10px 5px 10px',
            }}
          >
            <h1 style={{ marginBottom: '0px' }}>Find a biotech startup job.</h1>
            <p style={{ marginBottom: '0px', marginTop: '5px' }}>
              <b>{numberOfJobsAll}</b> jobs from <b>{companies.length}</b> companies. Updated on <b>{local}</b>.
            </p>
            <p style={{ marginTop: '0px' }}>
              Never miss out on new jobs. Join our{' '}
              <a style={{ cursor: 'pointer' }} onClick={() => this.toggleNewsletterModal(true)}>
                weekly update
              </a>
              !
            </p>

            <FeaturedCompanies companies={featuredCompanies} slotInfo={companiesSlotInfo} />

            <FeaturedJobs jobs={featuredJobs} slotInfo={jobsSlotInfo} />
          </Box>

          <Box
            sx={{
              display: { xs: 'flex', md: 'inline-flex' },
              verticalAlign: 'middle',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              paddingTop: { xs: 4, md: 6 },
            }}
          >
            <FormControl
              sx={{
                margin: { xs: '6px 12px', md: '10px 12px' },
                width: { xs: '90%', md: '280px' },
              }}
              variant="outlined"
              disabled={this.props.companyInfo.scriptName === 'script-3-rest'}
            >
              <InputLabel id="select-category-outlined-label">Select role(s)</InputLabel>
              <Select
                fullWidth
                labelId="select-category-outlined-label"
                id="select-category-select-outlined"
                multiple
                value={
                  this.props.companyInfo.scriptName === 'script-3-rest' ||
                  this.state.selectedCategorySlugs[0] === 'all-categories'
                    ? ['All roles']
                    : this.state.selectedCategorySlugs
                }
                onChange={this.handleChangeForCategorySelector}
                renderValue={selected => selected.map(categoryLabel).join(', ')}
                label="Select role(s)"
                MenuProps={filterSelectMenuProps}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'center',
                  },
                }}
              >
                <MenuItem
                  value="all-categories"
                  disabled={this.state.selectedCategorySlugs[0] === 'all-categories'}
                >
                  <ListItemIcon>
                    <Checkbox checked={this.state.selectedCategorySlugs.indexOf('all-categories') > -1} />
                  </ListItemIcon>
                  <ListItemText primary="All roles" />
                </MenuItem>
                {categories &&
                  categories.length > 0 &&
                  categories.map((c, i) =>
                    c.categorySlug === 'line' ? (
                      <hr key={c.categorySlug + i} />
                    ) : (
                      <MenuItem key={c.categorySlug} value={c.categorySlug}>
                        <ListItemIcon>
                          <Checkbox checked={this.state.selectedCategorySlugs.indexOf(c.categorySlug) > -1} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span>{c.categoryTitle}</span>
                              {c.salaryRange ? <span style={{ opacity: 0.75 }}>({c.salaryRange})</span> : null}
                            </span>
                          }
                        />
                      </MenuItem>
                    ),
                  )}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                margin: { xs: '6px 12px', md: '10px 12px' },
                width: { xs: '90%', md: '360px' },
              }}
              variant="outlined"
              disabled={this.props.companyInfo.scriptName === 'script-3-rest'}
            >
              <InputLabel id="state-selector-label">Select state(s)</InputLabel>
              <Select
                labelId="state-selector-label"
                id="state-selector-outlined"
                multiple
                value={
                  selectedStates[0] === 'all-states' || this.props.companyInfo.scriptName === 'script-3-rest'
                    ? ['All states']
                    : selectedStates
                }
                onChange={this.handleChangeForStateSelector}
                renderValue={selected => selected.join(', ')}
                label="Select state(s)"
                MenuProps={filterSelectMenuProps}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'center',
                  },
                }}
              >
                {states.map((state, i) =>
                  state === 'line' ? (
                    <hr key={state + i} />
                  ) : (
                    <MenuItem
                      key={state}
                      value={state}
                      disabled={state === 'all-states' && selectedStates[0] === 'all-states'}
                    >
                      <ListItemIcon>
                        <Checkbox checked={selectedStates.indexOf(state) > -1} />
                      </ListItemIcon>
                      <ListItemText primary={state === 'all-states' ? 'All states' : state} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                margin: { xs: '6px 12px', md: '10px 12px' },
                width: { xs: '90%', md: '380px' },
              }}
              variant="outlined"
            >
              <InputLabel id="select-company-outlined-label">Select company</InputLabel>
              <Select
                fullWidth
                labelId="select-company-outlined-label"
                id="select-company-select-outlined"
                multiple
                value={
                  this.state.selectedCompanySlugs[0] === 'all-companies'
                    ? ['All companies']
                    : this.state.selectedCompanySlugs
                }
                onChange={this.handleChangeForCompanySelector}
                renderValue={selected => selected.map(slug => companyLabel(slug, companies)).join(', ')}
                label="Select company"
                MenuProps={filterSelectMenuProps}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'center',
                  },
                }}
              >
                <MenuItem
                  value="all-companies"
                  disabled={this.state.selectedCompanySlugs[0] === 'all-companies'}
                >
                  <ListItemIcon>
                    <Checkbox checked={this.state.selectedCompanySlugs.indexOf('all-companies') > -1} />
                  </ListItemIcon>
                  <ListItemText primary="All companies" />
                </MenuItem>
                <hr />
                {companies &&
                  companies.length > 0 &&
                  sortCompaniesLetterFirst(companies).map(c => (
                    <MenuItem key={c._id} value={c.companySlug}>
                      <ListItemIcon>
                        <Checkbox checked={this.state.selectedCompanySlugs.indexOf(c.companySlug) > -1} />
                      </ListItemIcon>
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: { xs: 'flex', md: 'flow' },
              verticalAlign: 'middle',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: '10px',
              paddingTop: '20px',
              marginBottom: '30px',
            }}
          >
            <TextField
              label="Search by Job Title"
              sx={{
                width: { xs: '90%', md: '400px' },
                paddingRight: '10px',
                verticalAlign: 'middle',
              }}
              value={search}
              onKeyDown={e => {
                if (e.keyCode === 13) {
                  this.handleSearch();
                }
              }}
              onChange={e => this.setState({ search: e.target.value })}
            />

            <Button
              onClick={this.handleSearch}
              variant="contained"
              size="large"
              color="primary"
              sx={{ width: { xs: '90%', md: 'auto' } }}
            >
              Search
            </Button>
          </Box>
          <Box
            sx={{
              margin: { xs: '10px 2% -5px 1%', md: '10px 20% 10px 20%' },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' },
              justifyContent: { xs: 'center', md: 'space-between' },
              gap: '10px',
              width: { xs: '100%', md: 'auto' },
              boxSizing: 'border-box',
            }}
          >
            <Pagination
              page={page}
              count={count}
              shape="rounded"
              sx={{
                display: 'inline-flex',
                '& .MuiPagination-ul': {
                  justifyContent: { xs: 'center', md: 'flex-start' },
                },
              }}
              onChange={this.handleChangePageForMainPage}
              size="medium"
            />

            {!isDataLoaded ? null : (
              <div>
                {this.props.companyInfo.scriptName === 'script-3-rest' ? (
                  ''
                ) : (
                  <span>
                    <b>{numberOfJobs}</b> job(s){' '}
                  </span>
                )}
              </div>
            )}
          </Box>
          <p />
          {!isDataLoaded ? (
            <>
              <br />
              <p>Loading jobs...</p>
            </>
          ) : isDataLoaded && batch && batch.length > 0 ? (
            batch.map((j, i) => (
              <Box key={j._id} sx={{ position: 'relative' }}>
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    left: `calc(20% - (6px * ${((page - 1) * jobsPerPage + i + 1).toString().length}) - 15px)`,
                    top: '0px',
                    lineHeight: 1,
                  }}
                >
                  {(page - 1) * jobsPerPage + i + 1}.
                </Box>
                <JobDetail
                  job={j as any}
                  onCompanyNameClick={() => this.showCompanyModal(j.companyId)}
                  companyInfo={
                    this.state.selectedCompanySlugs.length === 1 &&
                    this.state.selectedCompanySlugs[0] !== 'all-companies'
                      ? companyInfo
                      : null
                  }
                  page="index"
                />
              </Box>
            ))
          ) : (
            <p>
              No jobs found.
              {emptyStateCompanyLinks.length > 0 ? (
                <>
                  {' '}
                  Check company page(s) for:{' '}
                  {emptyStateCompanyLinks.map((company, index) => (
                    <React.Fragment key={company.companySlug}>
                      {index > 0 ? ', ' : null}
                      <a target="_blank" rel="noopener noreferrer" href={`/companies/${company.companySlug}`}>
                        {company.name}
                      </a>
                    </React.Fragment>
                  ))}
                  .
                </>
              ) : null}
            </p>
          )}
          <p />
          <h3
            style={{
              textAlign: 'center',
            }}
          >
            {error ? error : null}
          </h3>
          <p
            style={{
              marginBottom: '40px',
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'center' } }}>
            <Pagination
              page={page}
              count={count}
              shape="rounded"
              sx={{
                display: 'inline-flex',
                '& .MuiPagination-ul': {
                  justifyContent: { xs: 'center', md: 'center' },
                },
              }}
              onChange={this.handleChangePageForMainPage}
              size="medium"
            />
          </Box>
          <p
            style={{
              paddingBottom: '180px',
            }}
          />
          <CompanyModal
            open={this.state.showCompanyModal}
            onClose={this.handleCompanyModalClose}
            company={this.state.selectedCompany}
            onShowAllJobsClick={() => this.showAllJobsForCompany(this.state.selectedCompany)}
          />
          <WeeklyUpdateModal open={this.state.openNewsletterModal} onClose={this.handleWeeklyUpdateModalClose} />
        </div>
      </Layout>
    );
  }

  public toggleNewsletterModal = (openNewsletterModal: boolean): void => {
    this.setState({ openNewsletterModal });
  };

  public handleWeeklyUpdateModalClose = () => {
    this.setState({ openNewsletterModal: false });
  };

  private handleChangeForStateSelector = event => {
    const value = event.target.value;
    const { selectedCategorySlugs, selectedCompanySlugs, search } = this.state;

    const lastSelected = value.length > 0 ? value[value.length - 1] : '';
    const isAllIncludedLast = lastSelected === 'All states' || lastSelected === 'all-states';

    const hasAllStates = value.includes('All states') || value.includes('all-states');
    const totalStateCount = states.filter(s => s !== 'line' && s !== 'all-states').length;
    const isAllImplied = value.length > 0 && !hasAllStates && value.length === totalStateCount;

    const isAllIncludedButNotLast = hasAllStates && !isAllIncludedLast;

    let finalStates = [];
    if (isAllIncludedLast || isAllImplied) {
      finalStates = ['all-states']; // Normalize to a single format
    } else if (isAllIncludedButNotLast) {
      finalStates = value.filter(s => s !== 'All states' && s !== 'all-states');
    } else {
      finalStates = value;
    }

    this.setState({
      isDataLoaded: false,
      selectedStates: finalStates,
    });

    const query: { category?: string; company?: string; state?: string; search?: string } = {};

    if (selectedCategorySlugs[0] !== 'all-categories') {
      query.category = selectedCategorySlugs.join(',');
    }

    if (selectedCompanySlugs[0] !== 'all-companies') {
      query.company = selectedCompanySlugs.join(',');
    }

    if (finalStates.length > 0 && !finalStates.includes('all-states')) {
      query.state = finalStates.join(',');
    }

    if (search) {
      query.search = search;
    }

    Router.push({ pathname: '/', query }, undefined, { scroll: false });
  };

  private handleChangeForCategorySelector = event => {
    const value = event.target.value as string[];
    const { selectedCompanySlugs, selectedStates, search } = this.state;

    const lastSelected = value.length > 0 ? value[value.length - 1] : '';
    const isAllIncludedLast = lastSelected === 'All roles' || lastSelected === 'all-categories';

    const hasAllCategories = value.includes('All roles') || value.includes('all-categories');
    const totalCategoryCount = categories.filter(c => c.categorySlug !== 'line').length;
    const isAllImplied = value.length > 0 && !hasAllCategories && value.length === totalCategoryCount;

    const isAllIncludedButNotLast = hasAllCategories && !isAllIncludedLast;

    let finalCategories: string[] = [];
    if (isAllIncludedLast || isAllImplied) {
      finalCategories = ['all-categories'];
    } else if (isAllIncludedButNotLast) {
      finalCategories = value.filter(s => s !== 'All roles' && s !== 'all-categories');
    } else {
      finalCategories = value;
    }

    this.setState({
      isDataLoaded: false,
      selectedCategorySlugs: finalCategories,
    });

    const query: { category?: string; company?: string; state?: string; search?: string } = {};

    if (finalCategories[0] !== 'all-categories') {
      query.category = finalCategories.join(',');
    }

    if (selectedCompanySlugs[0] !== 'all-companies') {
      query.company = selectedCompanySlugs.join(',');
    }

    if (selectedStates.length > 0 && !selectedStates.includes('all-states')) {
      query.state = selectedStates.filter(s => s !== 'All states').join(',');
    }

    if (search) {
      query.search = search;
    }

    Router.push({ pathname: '/', query }, undefined, { scroll: false });
  };

  private handleChangeForCompanySelector = event => {
    const value = event.target.value as string[];
    const { selectedCategorySlugs, selectedStates, search } = this.state;

    const lastSelected = value.length > 0 ? value[value.length - 1] : '';
    const isAllIncludedLast = lastSelected === 'All companies' || lastSelected === 'all-companies';

    const hasAllCompanies = value.includes('All companies') || value.includes('all-companies');
    const totalCompanyCount = this.props.companies.length;
    const isAllImplied = value.length > 0 && !hasAllCompanies && value.length === totalCompanyCount;

    const isAllIncludedButNotLast = hasAllCompanies && !isAllIncludedLast;

    let finalCompanies: string[] = [];
    if (isAllIncludedLast || isAllImplied) {
      finalCompanies = ['all-companies'];
    } else if (isAllIncludedButNotLast) {
      finalCompanies = value.filter(s => s !== 'All companies' && s !== 'all-companies');
    } else {
      finalCompanies = value;
    }

    this.setState({
      isDataLoaded: false,
      selectedCompanySlugs: finalCompanies,
    });

    const query: { category?: string; company?: string; state?: string; search?: string } = {};

    if (selectedCategorySlugs[0] !== 'all-categories') {
      query.category = selectedCategorySlugs.join(',');
    }

    if (finalCompanies[0] !== 'all-companies') {
      query.company = finalCompanies.join(',');
    }

    if (selectedStates.length > 0 && !selectedStates.includes('all-states')) {
      query.state = selectedStates.filter(s => s !== 'All states').join(',');
    }

    if (search) {
      query.search = search;
    }

    Router.push({ pathname: '/', query }, undefined, { scroll: false });
  };

  // updated
  private handleChangePageForMainPage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    if (event) {
      event.preventDefault();
    }

    const { selectedCategorySlugs, selectedCompanySlugs, selectedStates, page, search } = this.state;

    // Early exit if the page hasn't changed
    if (newPage === page) return;

    this.setState({ isDataLoaded: false, page: newPage });

    const query: Record<string, string | number> = { page: newPage };

    if (search) {
      query.search = search;
    }
    if (selectedCategorySlugs[0] !== 'all-categories') {
      query.category = selectedCategorySlugs.join(',');
    }
    if (selectedCompanySlugs[0] !== 'all-companies') {
      query.company = selectedCompanySlugs.join(',');
    }
    if (selectedStates[0] !== 'all-states') {
      query.state = selectedStates.filter(s => s !== 'All states').join(',');
    }

    Router.push({ pathname: '/', query }, undefined, { scroll: false });
  };

  private handleCompanyModalClose = () => {
    this.setState({ showCompanyModal: false, selectedCompany: null });
  };

  private handleSearch = () => {
    const { search, selectedCategorySlugs, selectedCompanySlugs, selectedStates } = this.state;

    const category = selectedCategorySlugs[0] === 'all-categories' ? 'all-categories' : selectedCategorySlugs.join(',');
    const company = selectedCompanySlugs[0] === 'all-companies' ? 'all-companies' : selectedCompanySlugs.join(',');
    const stateParam =
      selectedStates[0] === 'all-states' ? 'all-states' : selectedStates.filter(s => s !== 'All states').join(',');

    Router.push(`/?search=${search || ''}&category=${category}&state=${stateParam}&company=${company}`, undefined, {
      scroll: false,
    });
  };

  private showCompanyModal = async companyId => {
    const { company } = await getCompanyApiMethod(companyId);

    this.setState({ showCompanyModal: true, selectedCompany: company });
  };

  private showAllJobsForCompany = async company => {
    Router.push(`/?category=all-categories&company=${company.companySlug}&state=all-states`, undefined, {
      scroll: false,
    });

    this.setState({
      selectedCompanySlugs: [company.companySlug],
      selectedCategorySlugs: ['all-categories'],
      selectedState: 'all-states',
      selectedStates: ['all-states'],
      showCompanyModal: false,
      selectedCompany: null,
    });
  };
}

export default MainPage;
